/* eslint-disable max-len */
/* eslint-disable no-restricted-globals */
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { SpinnerMedium } from '@tmov/design-system/icons';
import { Tokens } from '@tmov/design-system/theme/types';
import { notification } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { LoadingContainer } from '../../../../../../../components/LoadingContainer';
import { useFiltro } from '../../../../../../../contexts/FilaEmbarque/Filtro/hooks/useFiltro';
import { capitalizar } from '../../../../../../../helpers/capitalizar';
import { getDataHoraFormatada } from '../../../../../../../helpers/data-fns';
import apiNetCore from '../../../../../../../services/apiNetCore';
import apiShipper from '../../../../../../../services/apiShipper';
import StatusMotoristaEnum from '../../../../../Enum/StatusMotoristaEnum';
import TipoDocumentoEnum from '../../../../../Enum/TipoDocumentoEnum';
import {
  ButtonCancel,
  ButtonPrimary,
  Container,
  CustomModal,
  ModalContent,
  Option,
  Options,
  Tag,
} from './styles';
import TagEmbarcadoForm from './TagEmbarcadoForm';
import TagRemovidoForm from './TagRemovidoForm';
import TagStatusDefaultForm from './TagStatusDefaultForm';

const TagButton = ({
  detalhesDemanda,
  initialStatusInt,
  idLoteConjunto,
  isTransportadoraExterna,
  tagMotorista,
  setMotoristas,
  anexoNfe,
}) => {
  const [statusInt, setStatusInt] = useState(initialStatusInt);
  const [expanded, setExpanded] = useState(false);
  const [tagDataCarregamento, setTagDataCarregamento] = useState(moment());
  const [tagPeso, setTagPeso] = useState('');
  const [tagJustificativa, setTagJustificativa] = useState('');
  const [tagDocumentos, setTagDocumentos] = useState([]);
  const tagRef = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    status: null,
    content: '',
  });

  const { fetchFiltro } = useFiltro();

  const isNextState = (prevState, nextState) => {
    if (
      prevState === StatusMotoristaEnum.EM_TRANSITO &&
      nextState === StatusMotoristaEnum.NA_FILA
    ) {
      return true;
    }
    if (
      prevState === StatusMotoristaEnum.NA_FILA &&
      nextState === StatusMotoristaEnum.EMBARCADO
    ) {
      return true;
    }
    if (
      prevState === StatusMotoristaEnum.EMBARCADO &&
      nextState === StatusMotoristaEnum.ENTREGUE
    ) {
      return true;
    }
    return false;
  };

  const options = useMemo(() => {
    const filteredOptions = StatusMotoristaEnum.getTiposLabel()
      .filter(option => option.value !== statusInt)
      .filter(
        option => option.value !== StatusMotoristaEnum.AGUARDANDO_APROVACAO
      )
      .filter(option => option.value !== StatusMotoristaEnum.DESISTIU);

    return filteredOptions.map(option => ({
      ...option,
      disabled:
        (option.value !== StatusMotoristaEnum.REMOVIDO &&
          !isNextState(statusInt, option.value)) ||
        (option.value === StatusMotoristaEnum.REMOVIDO &&
          statusInt === StatusMotoristaEnum.EMBARCADO) ||
        (option.value === StatusMotoristaEnum.EMBARCADO &&
          tagMotorista.HasDocumentoNFe &&
          !isTransportadoraExterna) ||
        (option.value === StatusMotoristaEnum.EMBARCADO &&
          !isTransportadoraExterna &&
          !anexoNfe),
    }));
  }, [isTransportadoraExterna, statusInt]);

  const handleClickOutside = event => {
    if (tagRef.current && !tagRef.current.contains(event.target)) {
      setExpanded(false);
    }
  };

  const handleChangeStatus = useCallback(
    tipo => {
      setExpanded(false);
      setModalVisible(true);

      if (tipo.value === StatusMotoristaEnum.EMBARCADO) {
        setModalInfo({
          status: tipo,
          content: (
            <TagEmbarcadoForm
              tagDataCarregamento={tagDataCarregamento}
              setTagDataCarregamento={setTagDataCarregamento}
              setTagPeso={setTagPeso}
              setTagDocumentos={setTagDocumentos}
              anexoNfe={anexoNfe}
              isTransportadoraExterna={isTransportadoraExterna}
            />
          ),
        });
        return;
      }

      if (tipo.value === StatusMotoristaEnum.REMOVIDO) {
        setModalInfo({
          status: tipo,
          content: (
            <TagRemovidoForm
              tagJustificativa={tagJustificativa}
              setTagJustificativa={setTagJustificativa}
            />
          ),
        });
        return;
      }

      setModalInfo({
        status: tipo,
        content: <TagStatusDefaultForm status={tipo.value} />,
      });
    },
    [tagDataCarregamento, tagJustificativa, tagPeso, tagDocumentos]
  );

  const getNotification = (hasAnexoNfe = false) => {
    const communProps = {
      message: 'O Status do motorista foi atualizado',
      className: 'custom-class',
      style: {
        width: 568,
        background: Tokens.eixods.colors.brand.primary['5'],
        borderLeftStyle: 'solid',
        borderLeftWidth: '4px',
        borderLeftColor: Tokens.eixods.colors.feedback.success,
      },
    };
    return hasAnexoNfe
      ? notification.success({
          ...communProps,
          description:
            'A NF-e foi enviada para a transportadora. Você pode acessar o arquivo na aba de documentos da viagem.',
        })
      : notification.success({
          ...communProps,
          description: 'O status atual do motorista é "Embarcado"',
        });
  };

  const atualizarStatusMotorista = useCallback(
    async status => {
      setConfirmLoading(true);

      try {
        if (status === StatusMotoristaEnum.EMBARCADO) {
          const dataFormatada = getDataHoraFormatada(tagDataCarregamento);

          const formData = new FormData();
          formData.append('IdLoteConjunto', idLoteConjunto);
          formData.append('Status', status);
          formData.append('Peso', tagPeso);
          formData.append('DataEmissao', dataFormatada);

          if (anexoNfe) {
            formData.append('AnexoNfe', anexoNfe);
            formData.append('TipoDocumento', TipoDocumentoEnum.NFE);

            Object.keys(tagDocumentos).forEach(i => {
              formData.append('Arquivos', tagDocumentos[i].arquivo);
            });
          }
          const response = await apiNetCore.post(
            'LoteConjuntoAts/AlterarStatusMotoristaTransportadoraComDocumentos',
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );
          if (response.status === 200) {
            getNotification(anexoNfe);
          }
        } else if (status === StatusMotoristaEnum.REMOVIDO) {
          await apiShipper.post('alterar-status-motorista', {
            idLoteConjunto,
            idFilial: tagMotorista.IdFilial,
            status,
            justificativa: tagJustificativa,
            motoristaNome: tagMotorista.Nome,
            placa: tagMotorista.PlacaCavalo,
            clienteRazaoSocial: capitalizar(
              detalhesDemanda.razaoSocialClientePagador
            ),
            origem: `${capitalizar(
              detalhesDemanda.enderecoExpedidor[0].cidade
            )} (${detalhesDemanda.enderecoExpedidor[0].estado})`,
            destino: `${capitalizar(
              detalhesDemanda.enderecoRecebedor[0].cidade
            )} (${detalhesDemanda.enderecoRecebedor[0].estado})`,
          });
          setMotoristas(motoristas =>
            motoristas.map(motorista => {
              if (motorista.IdLoteConjunto === idLoteConjunto) {
                return {
                  ...motorista,
                  statusInt: status,
                  justificativa: tagJustificativa,
                };
              }
              return motorista;
            })
          );
        } else {
          await apiShipper.post('alterar-status-motorista', {
            idLoteConjunto,
            status,
          });
          setMotoristas(motoristas =>
            motoristas.map(motorista => {
              if (motorista.IdLoteConjunto === idLoteConjunto) {
                return {
                  ...motorista,
                  statusInt: status,
                };
              }
              return motorista;
            })
          );
        }

        setStatusInt(status);
      } catch (error) {
        notification.error({
          message: 'Falha ao atualizar status do motorista.',
        });
      } finally {
        fetchFiltro(true);
      }
      setModalVisible(false);
      setConfirmLoading(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      detalhesDemanda,
      idLoteConjunto,
      setMotoristas,
      tagDataCarregamento,
      tagJustificativa,
      tagMotorista,
      tagPeso,
      tagDocumentos,
    ]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    setStatusInt(initialStatusInt);
  }, [initialStatusInt]);

  useEffect(() => {
    setTagDocumentos(tagDocumentos);
  }, [tagDocumentos, setTagDocumentos]);

  const handleModalCancelar = () => {
    setModalVisible(false);
  };

  const handleValidaButton = () => {
    if (!isTransportadoraExterna) {
      return modalInfo.status?.value === StatusMotoristaEnum.EMBARCADO
        ? anexoNfe && tagDocumentos.length <= 0
        : modalInfo.status?.value === StatusMotoristaEnum.REMOVIDO &&
            tagJustificativa === '';
    }
    return modalInfo.status?.value === StatusMotoristaEnum.EMBARCADO
      ? !tagDataCarregamento ||
          !tagPeso ||
          tagPeso < 10 ||
          tagPeso > 55 ||
          (anexoNfe && tagDocumentos.length <= 0)
      : modalInfo.status?.value === StatusMotoristaEnum.REMOVIDO &&
          tagJustificativa === '';
  };

  return (
    <>
      <Container
        ref={tagRef}
        onClick={() =>
          (statusInt !== StatusMotoristaEnum.ENTREGUE ||
            statusInt !== StatusMotoristaEnum.REMOVIDO) &&
          setExpanded(!expanded)
        }
        disabled={
          statusInt === StatusMotoristaEnum.ENTREGUE ||
          statusInt === StatusMotoristaEnum.REMOVIDO
        }
      >
        <Tag
          backgroundColor={StatusMotoristaEnum.getTipo(statusInt).background}
          textColor={StatusMotoristaEnum.getTipo(statusInt).textColor}
        >
          {StatusMotoristaEnum.getTipo(statusInt).label}
        </Tag>
        {statusInt !== StatusMotoristaEnum.ENTREGUE &&
          statusInt !== StatusMotoristaEnum.REMOVIDO && (
            <>{expanded ? <UpOutlined /> : <DownOutlined />}</>
          )}
        {expanded && (
          <Options>
            {options.map(option => (
              <Option
                key={option.value}
                onClick={() => !option.disabled && handleChangeStatus(option)}
                disabled={option.disabled}
              >
                <Tag
                  backgroundColor={
                    StatusMotoristaEnum.getTipo(option.value).background
                  }
                  textColor={
                    StatusMotoristaEnum.getTipo(option.value).textColor
                  }
                  disabled={option.disabled}
                >
                  {StatusMotoristaEnum.getTipo(option.value).label}
                </Tag>
              </Option>
            ))}
          </Options>
        )}
      </Container>

      <CustomModal
        closable={false}
        visible={modalVisible}
        destroyOnClose={true}
        footer={[
          <ButtonCancel
            key="cancelar"
            onClick={() => handleModalCancelar()}
            disabled={confirmLoading}
          >
            Cancelar
          </ButtonCancel>,

          <ButtonPrimary
            key="concluir"
            onClick={() => atualizarStatusMotorista(modalInfo.status?.value)}
            disabled={handleValidaButton() || confirmLoading}
          >
            Concluir
          </ButtonPrimary>,
        ]}
      >
        {confirmLoading && (
          <LoadingContainer height="10vh">
            <SpinnerMedium size="50px" />
          </LoadingContainer>
        )}

        <ModalContent>{modalInfo.content}</ModalContent>
      </CustomModal>
    </>
  );
};

const endereco = PropTypes.shape({
  bairro: PropTypes.string,
  cep: PropTypes.string,
  cidade: PropTypes.string,
  cnpj: PropTypes.string,
  complemento: PropTypes.string,
  endereco: PropTypes.string,
  estado: PropTypes.string,
  ibgeCidade: PropTypes.number,
  idCidade: PropTypes.number,
  idCliente: PropTypes.number,
  idClienteEndereco: PropTypes.number,
  idEstado: PropTypes.number,
  nomeCliente: PropTypes.string,
  numero: PropTypes.number,
});

TagButton.propTypes = {
  detalhesDemanda: PropTypes.shape({
    enderecoDestino: PropTypes.arrayOf(endereco),
    enderecoExpedidor: PropTypes.arrayOf(endereco),
    enderecoOrigem: PropTypes.arrayOf(endereco),
    enderecoRecebedor: PropTypes.arrayOf(endereco),
    razaoSocialClientePagador: PropTypes.string,
  }),
  initialStatusInt: PropTypes.number.isRequired,
  idLoteConjunto: PropTypes.number.isRequired,
  isTransportadoraExterna: PropTypes.bool.isRequired,
  tagMotorista: PropTypes.shape({
    IdLoteConjunto: PropTypes.number,
    IdFilial: PropTypes.number,
    ClassificacaoMotorista: PropTypes.number,
    EntradaFila: PropTypes.string,
    EntradaFilaData: PropTypes.string,
    Nome: PropTypes.string,
    PlacaCavalo: PropTypes.string,
    Status: PropTypes.string,
    StatusInt: PropTypes.number,
    TipoDirecionamento: PropTypes.number,
    HasDocumentoNFe: PropTypes.bool,
  }),
  setMotoristas: PropTypes.func.isRequired,
  anexoNfe: PropTypes.bool,
};

TagButton.defaultProps = {
  detalhesDemanda: null,
  tagMotorista: null,
  anexoNfe: false,
};

export default TagButton;
